/**
 * iter344 — Top-level Stocktake Reports page (/stocktaking/reports).
 *
 * Three-step picker that drives the same StocktakeReportsDialog used
 * inline inside the wizard:
 *   1. Select a client (parent or independent)
 *   2. Select a Stocktake or Valuation Session for that client
 *   3. Open the Reports dialog (filters + PDF download)
 *
 * Multi-location sessions surface their location chips in the dialog
 * step, mirroring the wizard flow.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, FileText, ArrowLeft, Search, Building2, Package } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { clientsService, stocktakeSessionsService } from '../services';
import api from '../services/api';
import StocktakeReportsDialog from '../components/StocktakeReportsDialog';

export default function StocktakeReportsPage() {
  const { toast } = useToast();
  const [step, setStep] = useState(1); // 1=client, 2=session, 3=dialog
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sites, setSites] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionSearch, setSessionSearch] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // ── Step 1: load clients + groups + sites ──────────────────
  // iter345 — also load sites so the picker can find by site name,
  // not just by client/group. Sites are a flat list with their
  // parent client wired in for downstream session lookup.
  useEffect(() => {
    setClientsLoading(true);
    Promise.all([
      clientsService.list({ page_size: 1000 }).catch(() => ({ items: [] })),
      clientsService.listGroups({ page_size: 500 }).catch(() => ({ items: [] })),
      api.get('/sites', { params: { page_size: 1000 } })
        .then(r => r.data?.items || r.data || [])
        .catch(() => []),
    ])
      .then(([cl, gp, st]) => {
        setClients(cl?.items || cl || []);
        setGroups(gp?.items || gp || []);
        setSites(st || []);
      })
      .catch(err => toast({
        title: 'Could not load clients',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      }))
      .finally(() => setClientsLoading(false));
  }, [toast]);

  const filteredClients = useMemo(() => {
    const term = clientSearch.trim().toLowerCase();
    const merged = [
      ...groups.map(g => ({ ...g, kind: 'group' })),
      ...clients.map(c => ({ ...c, kind: 'client' })),
      // iter345 — include sites in the picker so searching for
      // "Mercure Brighton" finds the site directly even though the
      // user typed nothing about its parent client.
      ...sites.map(s => ({
        id: s.id,
        name: s.name,
        region: s.region,
        kind: 'site',
        site_id: s.id,
        parent_client_id: s.parent_client_id,
      })),
    ];
    if (!term) {
      // Default view: only clients + groups (sites are a search-only
      // expansion to keep the default list scannable).
      return merged.filter(m => m.kind !== 'site');
    }
    return merged.filter(c =>
      (c.name || '').toLowerCase().includes(term)
      || (c.region || '').toLowerCase().includes(term)
    );
  }, [clients, groups, sites, clientSearch]);

  // ── Step 2: load sessions for selected client ──────────────
  useEffect(() => {
    if (!selectedClient || step !== 2) return;
    setSessionsLoading(true);
    const params = { page_size: 200 };
    if (selectedClient.kind === 'site') {
      // iter345 — single-site path: session list scoped by site_id.
      stocktakeSessionsService.list({ site_id: selectedClient.id, page_size: 200 })
        .then(res => {
          const items = res?.items || res || [];
          items.sort((a, b) => (b.session_date || '').localeCompare(a.session_date || ''));
          setSessions(items);
        })
        .catch(err => toast({
          title: 'Could not load sessions',
          description: err?.response?.data?.detail || err.message,
          variant: 'destructive',
        }))
        .finally(() => setSessionsLoading(false));
      return;
    }
    if (selectedClient.kind === 'group') {
      params.parent_client_id = selectedClient.id;
    } else {
      params.client_id = selectedClient.id;
    }
    api.get('/sites', { params })
      .then(res => {
        const siteIds = (res.data?.items || res.data || []).map(s => s.id);
        if (siteIds.length === 0) {
          setSessions([]);
          return;
        }
        return Promise.all(siteIds.map(sid =>
          stocktakeSessionsService.list({ site_id: sid, page_size: 100 }).catch(() => ({ items: [] }))
        )).then(results => {
          const all = [];
          for (const r of results) {
            const items = r?.items || r || [];
            all.push(...items);
          }
          all.sort((a, b) => (b.session_date || '').localeCompare(a.session_date || ''));
          setSessions(all);
        });
      })
      .catch(err => toast({
        title: 'Could not load sessions',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      }))
      .finally(() => setSessionsLoading(false));
  }, [selectedClient, step, toast]);

  const filteredSessions = useMemo(() => {
    const term = sessionSearch.trim().toLowerCase();
    if (!term) return sessions;
    return sessions.filter(s =>
      (s.session_reference || '').toLowerCase().includes(term)
      // iter345 — session list returns site_name as a flat field
      // (not s.site.name); search against both for resilience.
      || (s.site_name || s.site?.name || '').toLowerCase().includes(term)
      || (s.session_type || '').toLowerCase().includes(term)
    );
  }, [sessions, sessionSearch]);

  const reset = () => {
    setStep(1);
    setSelectedClient(null);
    setSelectedSession(null);
    setSessionSearch('');
    setDialogOpen(false);
  };

  return (
    <div className="space-y-4" data-testid="stocktake-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5" /> Report Centre
          </h1>
          <p className="text-sm text-muted-foreground">
            Summary, Core Stock Audit, Valuation and Supporting Schedules — pick a client, then a session, then the report.
          </p>
        </div>
        {step > 1 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (step === 3) { setDialogOpen(false); setStep(2); }
              else if (step === 2) { setSelectedClient(null); setStep(1); }
            }}
            data-testid="reports-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />Back
          </Button>
        )}
      </div>

      {/* Stepper indicator */}
      <div className="flex items-center gap-2 text-xs">
        {[1, 2, 3].map(n => (
          <React.Fragment key={n}>
            <div
              className={`px-2 py-1 rounded-full font-medium ${
                step === n ? 'bg-blue-700 text-white' : step > n ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
              }`}
              data-testid={`reports-step-${n}-indicator`}
            >
              {n}. {n === 1 ? 'Client' : n === 2 ? 'Session' : 'Report'}
            </div>
            {n < 3 && <div className="h-px w-6 bg-slate-300" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Client */}
      {step === 1 && (
        <Card data-testid="reports-step-client">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" />Select a client
            </CardTitle>
            <CardDescription className="text-xs">
              Showing {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''} (groups + standalone).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative mb-3">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search by client name or region…"
                value={clientSearch}
                onChange={e => setClientSearch(e.target.value)}
                className="pl-7 h-9 text-sm"
                data-testid="reports-client-search"
              />
            </div>
            {clientsLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[60vh] overflow-y-auto pr-1">
                {filteredClients.map(c => (
                  <button
                    key={`${c.kind}-${c.id}`}
                    type="button"
                    onClick={() => { setSelectedClient(c); setStep(2); }}
                    className="flex flex-col items-start gap-0.5 px-3 py-2 rounded border border-slate-200 hover:border-blue-500 hover:bg-blue-50/40 text-left transition-colors"
                    data-testid={`reports-client-${c.kind}-${c.id}`}
                  >
                    <div className="flex items-center gap-1.5 w-full">
                      <span className="font-medium text-sm flex-1 truncate">{c.name}</span>
                      {c.kind === 'group' && (
                        <span className="text-[9px] px-1 rounded bg-purple-100 text-purple-700">GROUP</span>
                      )}
                      {c.kind === 'site' && (
                        <span className="text-[9px] px-1 rounded bg-emerald-100 text-emerald-700">SITE</span>
                      )}
                    </div>
                    {c.region && (
                      <span className="text-[10px] text-muted-foreground">{c.region}</span>
                    )}
                  </button>
                ))}
                {filteredClients.length === 0 && (
                  <div className="col-span-full text-center text-sm text-muted-foreground py-6">
                    No clients match.
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Session */}
      {step === 2 && selectedClient && (
        <Card data-testid="reports-step-session">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="h-4 w-4" />Select a session for {selectedClient.name}
            </CardTitle>
            <CardDescription className="text-xs">
              Stocktake and Valuation sessions across all sites under this client.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative mb-3">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search by reference, site, or type…"
                value={sessionSearch}
                onChange={e => setSessionSearch(e.target.value)}
                className="pl-7 h-9 text-sm"
                data-testid="reports-session-search"
              />
            </div>
            {sessionsLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading sessions…
              </div>
            ) : filteredSessions.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No sessions found for this client.
              </div>
            ) : (
              <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
                {filteredSessions.map(s => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => { setSelectedSession(s); setStep(3); setDialogOpen(true); }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded border border-slate-200 hover:border-blue-500 hover:bg-blue-50/40 text-left transition-colors"
                    data-testid={`reports-session-${s.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm flex items-center gap-2">
                        <span className="font-mono text-xs text-slate-500">{s.session_reference || `#${s.id}`}</span>
                        {/* iter345 — sessions list returns site_name (string), not site object */}
                        <span className="truncate">{s.site_name || s.site?.name || 'Unknown site'}</span>
                      </div>
                      <div className="text-[10px] text-muted-foreground flex items-center gap-2">
                        <span className="capitalize">{(s.session_type || '').replace(/_/g, ' ')}</span>
                        <span>•</span>
                        <span>{s.session_date || '—'}</span>
                        <span>•</span>
                        <span className="capitalize">{(s.status || '').replace(/_/g, ' ')}</span>
                      </div>
                    </div>
                    <FileText className="h-4 w-4 text-blue-600" />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 3: Reports dialog (re-uses the wizard component) */}
      <StocktakeReportsDialog
        open={dialogOpen && step === 3}
        onOpenChange={(v) => {
          setDialogOpen(v);
          if (!v && step === 3) setStep(2);
        }}
        sessionId={selectedSession?.id}
        onBack={() => setStep(2)}
      />
    </div>
  );
}
